import React from "react";
import { X, ExternalLink, ArrowRight } from "lucide-react";
import BrowserFrame from "./ui/BrowserFrame";
import { useEscapeKey } from "../../hooks/useEscapeKey";

// Lock body scroll while the modal is open.
function useLockBodyScroll(locked) {
  React.useEffect(() => {
    if (!locked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [locked]);
}

/**
 * CaseStudyModal — premium project detail viewer (Dialog-style).
 */
const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] tracking-[0.32em] uppercase text-gold/80 mb-2">
      {label}
    </div>
    <div className="text-white/80 text-[15px] leading-relaxed">{children}</div>
  </div>
);

const OpenSlotMedia = () => (
  <div
    className="relative flex flex-col items-center justify-center py-20 rounded-xl"
    style={{
      border: "1px solid rgba(212,175,55,0.25)",
      background:
        "radial-gradient(ellipse at center, rgba(212,175,55,0.10), transparent 70%)",
    }}
  >
    <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
      Open Slot
    </div>
    <div className="font-serif text-3xl text-white max-w-md text-center">
      Your brand could live here.
    </div>
  </div>
);

const CaseStudyHeader = ({ project }) => (
  <div className="p-6 sm:p-8 pb-0">
    {project.thumb ? (
      <BrowserFrame url={project.url} src={project.thumb} alt={project.name} />
    ) : (
      <OpenSlotMedia />
    )}
  </div>
);

const CaseStudyActions = ({ project, onClose, onRequestBuild }) => {
  const isOpen = project.status === "open";
  return (
    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/5">
      {project.url && (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="case-study-visit"
          className="btn-gold inline-flex items-center gap-2 justify-center"
        >
          Visit Site <ExternalLink size={13} />
        </a>
      )}
      <button
        data-testid="case-study-request-build"
        onClick={() => {
          onClose?.();
          onRequestBuild?.();
        }}
        className="btn-ghost-gold inline-flex items-center gap-2 justify-center"
      >
        {isOpen ? "Start Your Personal Action Plan" : "Request Similar Build"}{" "}
        <ArrowRight size={13} />
      </button>
    </div>
  );
};

const CaseStudyBody = ({ project, onClose, onRequestBuild }) => (
  <div className="p-6 sm:p-10 space-y-10">
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {project.tags.map((t) => (
          <span
            key={t}
            className="text-[10px] tracking-[0.22em] uppercase text-gold/85 border border-gold/30 px-3 py-1 rounded-full"
          >
            {t}
          </span>
        ))}
      </div>
      <h3 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
        {project.name}
      </h3>
      {project.url && (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-3 text-[11px] tracking-[0.3em] uppercase text-gold/80 hover:text-gold"
        >
          {project.url.replace(/^https?:\/\//, "")} <ExternalLink size={12} />
        </a>
      )}
    </div>

    <Field label="The Brief">{project.brief}</Field>
    {project.before && <Field label="What Was Forced">{project.before}</Field>}
    {project.after && <Field label="What Got Aligned">{project.after}</Field>}

    {project.deliverables?.length > 0 && (
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-gold/80 mb-3">
          Deliverables
        </div>
        <div className="flex flex-wrap gap-2">
          {project.deliverables.map((d) => (
            <span
              key={d}
              className="text-xs text-white/70 border-l border-gold/40 pl-3 py-1"
            >
              {d}
            </span>
          ))}
        </div>
      </div>
    )}

    {project.outcome && <Field label="Outcome">{project.outcome}</Field>}

    <CaseStudyActions
      project={project}
      onClose={onClose}
      onRequestBuild={onRequestBuild}
    />
  </div>
);

const CaseStudyModal = ({ project, open, onClose, onRequestBuild }) => {
  useLockBodyScroll(open);
  useEscapeKey(onClose, open);

  if (!open || !project) return null;

  return (
    <div
      data-testid="case-study-modal"
      className="fixed inset-0 z-[90] flex items-start justify-center bg-black/85 backdrop-blur-md px-4 py-10 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full glass-card p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ borderRadius: "18px" }}
      >
        <button
          data-testid="case-study-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full border border-white/15 hover:border-gold/60 text-white/75 hover:text-gold flex items-center justify-center transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <CaseStudyHeader project={project} />
        <CaseStudyBody
          project={project}
          onClose={onClose}
          onRequestBuild={onRequestBuild}
        />
      </div>
    </div>
  );
};

export default CaseStudyModal;
